
import { supabase } from '@/integrations/supabase/client';

/**
 * Utility function to delete a file from storage by its URL
 */
export async function deleteTrackFile(fileUrl: string): Promise<{ success: boolean, error: Error | null }> {
  try {
    if (!fileUrl) {
      console.log("No file URL provided for deletion");
      return { success: false, error: new Error("No file URL provided") };
    }
    
    // Extract the path after the bucket name
    // File URL format: https://ovklrhkrsodpunvdyebr.supabase.co/storage/v1/object/public/uploads/artistId/filename
    const parts = fileUrl.split('/uploads/');
    if (parts.length < 2) {
      console.error("Could not determine file path from URL:", fileUrl);
      return { success: false, error: new Error("Could not determine file path from URL") };
    }
    
    const filePath = parts[1];
    console.log("Deleting file from storage at path:", filePath);
    
    // Delete the file from storage
    const { error: storageError } = await supabase.storage
      .from('uploads')
      .remove([filePath]);
      
    if (storageError) {
      console.error("Storage delete error:", storageError);
      // Continue with database deletion even if file deletion fails
      console.log("Continuing with database deletion despite storage error");
    } else {
      console.log("File successfully deleted from storage");
    }
    
    return { success: true, error: null };
  } catch (error) {
    console.error("Error deleting file:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error : new Error('Unknown error deleting file') 
    };
  }
}

export async function deleteTrack(trackId: string): Promise<{ success: boolean, error: Error | null }> {
  try {
    console.log("Starting delete process for track ID:", trackId);
    
    if (!trackId) {
      console.error("Invalid track ID provided for deletion");
      throw new Error("Invalid track ID provided for deletion");
    }
    
    // Get the track details to access the file path for deletion from storage
    const { data: track, error: fetchError } = await supabase
      .from('tracks')
      .select('*')
      .eq('id', trackId)
      .maybeSingle();
    
    if (fetchError) {
      console.error("Error fetching track for deletion:", fetchError);
      throw new Error(fetchError.message || "Failed to fetch track");
    }
    
    if (!track) {
      console.error("Track not found:", trackId);
      throw new Error("Track not found");
    }
    
    console.log("Found track to delete:", track.id, "with file URL:", track.file_url);
    
    // First delete any associated page views to avoid foreign key constraint issues
    console.log("Deleting associated page views for track:", trackId);
    const { error: pageViewsError } = await supabase
      .from('page_views')
      .delete()
      .eq('track_id', trackId);

    if (pageViewsError) {
      console.error("Error deleting page views:", pageViewsError);
      // Continue with database deletion even if page view deletion fails
      console.log("Continuing with track deletion despite page views error");
    } else {
      console.log("Successfully deleted associated page views");
    }
    
    // Delete the file from storage if it exists
    if (track.file_url) {
      const { success, error } = await deleteTrackFile(track.file_url);
      
      if (!success) {
        console.error("Failed to delete file, but continuing with database deletion:", error);
        // Continue with database deletion even if file deletion fails
      }
    }
    
    // Now delete the database record
    console.log("Deleting track record from database:", trackId);
    const { error: deleteError } = await supabase
      .from('tracks')
      .delete()
      .eq('id', trackId);
    
    if (deleteError) {
      console.error("Database deletion error:", deleteError);
      throw new Error(deleteError.message || "Failed to delete track");
    }
    
    console.log("Track successfully deleted:", trackId);
    return { success: true, error: null };
  } catch (error) {
    console.error("Track deletion error:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error : new Error('Unknown error deleting track') 
    };
  }
}
